import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'success' | 'warning';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

const buttonVariants = {
  default: 'bg-primary text-white hover:bg-primary-600 shadow-sm',
  destructive: 'bg-error text-white hover:bg-error/90',
  outline: 'border border-bordergray dark:border-dark-border bg-white dark:bg-dark-card hover:bg-surface dark:hover:bg-dark-hover text-dark dark:text-dark-text',
  secondary: 'bg-secondary-light dark:bg-secondary/20 text-secondary hover:bg-secondary/20 dark:hover:bg-secondary/30',
  ghost: 'hover:bg-surface dark:hover:bg-dark-hover text-muted dark:text-dark-muted hover:text-dark dark:hover:text-dark-text',
  link: 'text-primary dark:text-primary-400 underline-offset-4 hover:underline',
  success: 'bg-success text-white hover:bg-success/90',
  warning: 'bg-warning text-white hover:bg-warning/90',
};

const buttonSizes = {
  default: 'h-[42px] px-5 py-2.5',
  sm: 'h-[36px] px-4 py-2 text-[13px]',
  lg: 'h-[48px] px-6 py-3',
  icon: 'h-[42px] w-[42px]',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', ...props }, ref) => {
    return (
      <button
        className={cn(
          'inline-flex items-center justify-center whitespace-nowrap rounded-sm text-[14px] font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          buttonVariants[variant],
          buttonSizes[size],
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';

export { Button };
